import LRUCache from "./LRUCache";
import { SanitizerReturnType } from "./sanitizerReturnType";
import type { OwaSanitizerConfig } from "./owaSanitizerConfig";
import { shouldUseNativeSanitizer } from "./shouldUseNativeSanitizer";
import { sanitizeWithDomPurify } from "./domPurifySanitizer";
import { lazySanitizeWithNativeAPI } from "../lazyFunctions";
import { removeStyleComments } from "./removeStyleComments";
import { getSanitizerConfigKey } from "./adapters/sanitizerConfigKey";
import type { sanitizeWithNativeAPI as SanitizeWithNativeAPIType } from "./nativeSanitizer";

let _sanitizeWithNativeAPI: typeof SanitizeWithNativeAPIType | undefined;
const cacheSize = 1;

/**
 * Sanitizer caches, keyed in two levels.
 *
 * The outer key is the HTML body itself (or the caller's cache ID). Using the
 * caller's own string as the key — rather than concatenating it into a composite
 * key — matters a great deal at email-body sizes: a composite key allocates a
 * fresh multi-hundred-KB string on every call and stores a second copy of the
 * body inside each cache, whereas the body string is already live and the engine
 * caches its hash on the string object, so repeat lookups do not rehash it.
 *
 * The inner key distinguishes the sanitizer config and return type, which are
 * both short. Both levels are bounded to one body with one variant.
 */
type SanitizerVariantCache = LRUCache<string>;

/**
 * This cache is used to store sanitized HTML strings to avoid re-sanitizing the same HTML multiple times.
 * The number can increase if we see more cases of HTML sanitizing.
 */
let sanitizedStringCache: LRUCache<SanitizerVariantCache> | undefined;

/**
 * This cache is used to store html strings with style comments removed to avoid re-processing the same HTML multiple times.
 */
let styleCommentsCache: LRUCache<SanitizerVariantCache> | undefined;

function getVariants(cache: LRUCache<SanitizerVariantCache>, contentKey: string, maxVariants: number): SanitizerVariantCache {
    let variants = cache.get(contentKey);
    if (!variants) {
        variants = new LRUCache<string>(maxVariants);
        cache.set(contentKey, variants);
    }
    return variants;
}

// Type mapping for return types
type ReturnTypeMap = {
    [SanitizerReturnType.String]: string;
    [SanitizerReturnType.TrustedHTML]: string;
    [SanitizerReturnType.HTMLElement]: HTMLElement;
    [SanitizerReturnType.DocumentFragment]: DocumentFragment;
    [SanitizerReturnType.Node]: Node;
};

// Function overloads using generics
function getTrustedHTML<T extends SanitizerReturnType>(htmlString: string, returnType: T): ReturnTypeMap[T];
function getTrustedHTML<T extends SanitizerReturnType>(htmlString: string, returnType: T, extendedConfig: OwaSanitizerConfig, optionalCacheID?: string): ReturnTypeMap[T];

function getTrustedHTML<T extends SanitizerReturnType>(htmlString: string, returnType: T, extendedConfig: OwaSanitizerConfig = {}, optionalCacheID?: string): ReturnTypeMap[T] {
    if (!htmlString) {
        // If htmlString is not defined, default to empty string to avoid DOMPurify returning null
        htmlString = "";
    }
    
    // Two-level key: the body (or the caller's cache ID) identifies the content,
    // and a short variant key distinguishes config and return type. The body is
    // never concatenated into a composite key — see SanitizerVariantCache.
    const contentKey = optionalCacheID || (typeof htmlString === "string" ? htmlString : `${htmlString}`);
    const variantKey = `${getSanitizerConfigKey(extendedConfig)}:${returnType}`;
    
    // Define one single instance of cache if is undefined
    if (sanitizedStringCache === undefined) {
        sanitizedStringCache = new LRUCache<SanitizerVariantCache>(cacheSize);
    }
    
    // Define one single instance of styleCommentsCache if is undefined
    if (styleCommentsCache === undefined) {
        styleCommentsCache = new LRUCache<SanitizerVariantCache>(cacheSize);
    }
    
    const styleCommentVariants = getVariants(styleCommentsCache, contentKey, cacheSize);
    const cachedStyleComments = styleCommentVariants.get(variantKey);
    
    if (cachedStyleComments) {
        htmlString = cachedStyleComments;
    }
    else {
        // Remove style-tag comments before sanitizing and cache the value
        const processed = removeStyleComments(htmlString);
        styleCommentVariants.set(variantKey, processed);
        htmlString = processed;
    }
    
    const sanitizedVariants = getVariants(sanitizedStringCache, contentKey, cacheSize);
    
    // --- Dual-path dispatch: native Sanitizer API vs DOMPurify ---
    
    const useNative = shouldUseNativeSanitizer();
    
    if (useNative) {
        if (_sanitizeWithNativeAPI) {
            if (returnType === SanitizerReturnType.String ||
                returnType === SanitizerReturnType.TrustedHTML) {
                const sanitizedStringValue = sanitizedVariants.get(variantKey);
                if (sanitizedStringValue) {
                    return sanitizedStringValue as ReturnTypeMap[T];
                }
                
                const result = _sanitizeWithNativeAPI(htmlString, returnType, extendedConfig) as string;
                
                if (htmlString) {
                    sanitizedVariants.set(variantKey, result);
                }
                
                return result as ReturnTypeMap[T];
            }
            
            return _sanitizeWithNativeAPI(htmlString, returnType, extendedConfig) as ReturnTypeMap[T];
        }
        
        // Module not loaded yet — kick off load and store the function for subsequent calls.
        // LazyImport deduplicates internally so this is safe to call on every miss.
        lazySanitizeWithNativeAPI.import().then(sanitizeWithNativeAPI => {
            _sanitizeWithNativeAPI = sanitizeWithNativeAPI;
        });
    }
    
    // --- DOMPurify path (existing behavior) ---
    
    if (returnType === SanitizerReturnType.String ||
        returnType === SanitizerReturnType.TrustedHTML) {
        // Check if the sanitized html string is in the cache
        const sanitizedStringValue = sanitizedVariants.get(variantKey);
        
        if (sanitizedStringValue) {
            return sanitizedStringValue as ReturnTypeMap[T];
        }
        
        const trustedHTML = sanitizeWithDomPurify(htmlString, returnType, extendedConfig) as string;
        
        // Only cache when NOT expecting native, this is a temporary DOMPurify fallback
        // when useNative is true and _sanitizeWithNativeAPI is not yet available.
        if (htmlString && !useNative) {
            sanitizedVariants.set(variantKey, trustedHTML);
        }
        
        return trustedHTML as ReturnTypeMap[T];
    }
    
    return sanitizeWithDomPurify(htmlString, returnType, extendedConfig) as ReturnTypeMap[T];
}

export { getTrustedHTML, SanitizerReturnType };
